import { PieSeriesComponent } from '@swimlane/ngx-charts';

/**
 * Minimum vertical gap, in px, between two outside pie labels on the same side of a chart.
 *
 * ngx-charts hard-codes this to 10px, which suits its default ~11px label font. This app styles
 * `.pie-label` at 16px (see styles.scss), so two labels 10px apart overlap: the glyphs of a 16px
 * label span roughly 8px either side of the anchor. 20px leaves a visible gap between them.
 */
export const PIE_LABEL_MIN_DISTANCE = 20;

/** The shape ngx-charts hands to its label layout: `pos` is the label anchor [x, y] from the chart centre. */
export interface PositionedPieLabel {
  pos: [number, number];
}

/**
 * Same-side collision pass over outside pie labels, as ngx-charts does it, but with a configurable
 * gap. Labels are expected in slice order. Wherever two visible labels on the same side of the
 * chart sit closer than `minDistance` vertically, the later one is pushed further along the ring
 * (down on the right-hand side, up on the left-hand side) until the gap is restored. Hidden labels
 * neither move nor block others. Mutates and returns `labels`.
 */
export function spreadPieLabels<T extends PositionedPieLabel>(
  labels: T[],
  isVisible: (label: T) => boolean,
  minDistance: number
): T[] {
  for (let i = 0; i < labels.length - 1; i++) {
    const a = labels[i];
    if (!isVisible(a)) {
      continue;
    }
    for (let j = i + 1; j < labels.length; j++) {
      const b = labels[j];
      // Skip hidden labels and labels on the other side of the chart.
      if (!isVisible(b) || b.pos[0] * a.pos[0] <= 0) {
        continue;
      }
      const overlap = minDistance - Math.abs(b.pos[1] - a.pos[1]);
      if (overlap > 0) {
        b.pos[1] += Math.sign(b.pos[0]) * overlap;
      }
    }
  }
  return labels;
}

let applied = false;

/**
 * Widens the label collision gap used by every ngx-charts pie chart in the app from the library's
 * hard-coded 10px to `minDistance`. ngx-charts exposes no input for this, so the series component's
 * label layout is wrapped: the library still places the labels, then any same-side neighbours that
 * remain too close are spread apart. Only the first call takes effect; later calls are no-ops.
 */
export function applyPieLabelSpacing(minDistance: number = PIE_LABEL_MIN_DISTANCE): void {
  if (applied) {
    return;
  }
  applied = true;

  const original = PieSeriesComponent.prototype.calculateLabelPositions;
  PieSeriesComponent.prototype.calculateLabelPositions = function (this: PieSeriesComponent, pieData: any): any {
    const labels: PositionedPieLabel[] = original.call(this, pieData);
    return spreadPieLabels(labels, (label) => this.labelVisible(label), minDistance);
  };
}
